import { Injectable } from '@angular/core';
import { Location } from '@angular/common';

import { ofType, createEffect, Actions, OnInitEffects } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import {
  AlertService,
  NavigationService,
  MaintanceService
} from '@cpd-app/core-module/services';

import {
  getMaintance,
  getMaintanceFailure,
  getMaintanceSuccess
} from './maintance.actions';
import { ErrorCodeType } from '@cpd-app/shared-module/constants';

@Injectable()
export class MaintanceEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private maintanceService: MaintanceService,
    private alertService: AlertService,
    private navigationService: NavigationService,
    private location: Location
  ) {}

  /**
   * INFRA-4. The admin portal must survive a site block, because it is where
   * the block gets turned back off. Without this, switching the block on ejects
   * the admin from the only page that can switch it off, and recovery needs
   * manual SQL against the database.
   *
   * Reads Location rather than Router.url because this runs from
   * ngrxOnInitEffects at boot: Location reflects the browser's URL straight
   * away, while Router.url is still '/' until the initial navigation resolves,
   * which would make the exemption miss exactly when it matters most.
   *
   * Pairs with the siteBlockGuard removal on the /manager route. Both are
   * needed: the guard blocks direct navigation, this effect redirects from
   * anywhere, so removing only one leaves the lockout intact while looking
   * fixed.
   */
  private isAdminPortal(): boolean {
    // Exact segment match, not a prefix: a bare startsWith('/manager') would
    // also exempt any future sibling route like /manager-signup, quietly
    // letting a blocked learner sit on it.
    const path = this.location.path().split('?')[0];
    return path === '/manager' || path.startsWith('/manager/');
  }

  public ngrxOnInitEffects() {
    return getMaintance();
  }

  public getMaintance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getMaintance),
      switchMap(() => {
        return this.maintanceService.getMaintanceStatus().pipe(
          map((data) => getMaintanceSuccess({ response: data })),
          catchError((error) => of(getMaintanceFailure({ error })))
        );
      })
    );
  });

  public getMaintanceSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getMaintanceSuccess),
        tap(({ response }) => {
          if (response?.siteblocked && !this.isAdminPortal()) {
            this.navigationService.goToErrorPage(ErrorCodeType.Maintenance);
            return;
          }

          if (response?.banneractive && response?.message) {
            this.alertService.showAlert({
              message: response.message,
              autoClose: false
            });
          }
        })
      );
    },
    { dispatch: false }
  );
}
